import * as DomBuilder from "./dom-builder.js";
import * as Utils from "./utils.js";
import Transaction from "../Transaction.js";
import * as CsvWriter from "../csv-writer.js";

const INITIAL_ROW_LIMIT = 500;

/**
 *
 * @version 2026-09-04
 * @author Patrik Harag
 */
export default class ComponentAnalysisTable {

    /** @type {Controller} */
    #controller;
    /** @type {Transaction[]} */
    #transactions;
    /** @type {Filter} */
    #selectedFilter;
    /** @type {Map<string,Filter>} */
    #allFilters;

    #tableBuilder = DomBuilder.bootstrapTableBuilder();
    /** @type {number} */
    #pendingCount = 0;
    /** @type {HTMLElement|null} */
    #loadMoreButton = null;

    /**
     *
     * @param controller {Controller}
     * @param transactions {Transaction[]}
     * @param allFilters {Map<string,Filter>}
     * @param selectedFilter {Filter}
     */
    constructor(controller, transactions, allFilters, selectedFilter) {
        this.#controller = controller;
        this.#transactions = transactions;
        this.#allFilters = allFilters;
        this.#selectedFilter = selectedFilter;
    }

    createNode() {
        this.#build(this.#transactions, this.#allFilters, this.#selectedFilter);
        let tableNode = this.#tableBuilder.createNode();
        if (this.#pendingCount <= 0) {
            return tableNode;
        }

        this.#loadMoreButton = DomBuilder.button('Load more (' + this.#pendingCount + ')', { type: 'button', class: 'btn btn-secondary analysis-load-more' }, () => {
            this.#loadRemaining();
        });
        return DomBuilder.div(null, [
            tableNode,
            this.#loadMoreButton
        ]);
    }

    /**
     *
     * @return {string[][]}
     */
    toCsv() {
        let rows = [['Date', 'Description', 'Value', 'Cumulative']];
        let sum = 0;
        this.#transactions.slice().sort(Transaction.comparator).forEach(transaction => {
            sum += transaction.getValue();
            rows.push([
                transaction.getDate(),
                transaction.getDescription(),
                CsvWriter.formatNumber(transaction.getValue()),
                CsvWriter.formatNumber(sum)
            ]);
        });
        return rows;
    }

    /**
     *
     * @param transactions {Transaction[]}
     * @param filters {Map<string,Filter>}
     * @param filter {Filter}
     */
    #build(transactions, filters, filter) {
        let sorted = transactions.sort(Transaction.comparator);
        let visibleStart = Math.max(0, sorted.length - INITIAL_ROW_LIMIT);
        this.#pendingCount = visibleStart;

        let sum = 0;
        let seen = [];
        for (let i = 0; i < visibleStart; i++) {
            sum += sorted[i].getValue();
            seen.push(sorted[i]);
        }
        for (let i = visibleStart; i < sorted.length; i++) {
            let transaction = sorted[i];
            sum += transaction.getValue();
            seen.push(transaction);
            this.#tableBuilder.addRowBefore(this.#createRow(transaction, sum, seen, filters, filter));
        }
    }

    #loadRemaining() {
        if (this.#pendingCount <= 0) {
            return;
        }

        let filters = this.#allFilters;
        let filter = this.#selectedFilter;
        let sorted = this.#transactions;
        let rows = [];
        let sum = 0;
        let seen = [];
        for (let i = 0; i < this.#pendingCount; i++) {
            let transaction = sorted[i];
            sum += transaction.getValue();
            seen.push(transaction);
            rows.push(this.#createRow(transaction, sum, seen, filters, filter));
        }
        for (let i = rows.length - 1; i >= 0; i--) {
            this.#tableBuilder.addRow(rows[i]);
        }

        this.#pendingCount = 0;
        if (this.#loadMoreButton !== null) {
            this.#loadMoreButton.remove();
            this.#loadMoreButton = null;
        }
    }

    /**
     *
     * @param transaction {Transaction}
     * @param sum {number}
     * @param seen {Transaction[]}
     * @param filters {Map<string,Filter>}
     * @param filter {Filter}
     * @return {HTMLElement}
     */
    #createRow(transaction, sum, seen, filters, filter) {
        let descriptionCell;

        let row = DomBuilder.element('tr');
        row.append(DomBuilder.element('td', { class: 'group-key-cell' }, DomBuilder.span(transaction.getDate())));
        row.append(descriptionCell = DomBuilder.element('td', null, DomBuilder.span(transaction.getDescription())));
        row.append(DomBuilder.element('td', { class: `value-cell ${transaction.getValue() < 0 ? 'negative' : 'positive'}` },
                Utils.createValue(transaction.getValue(), Utils.formatFxTooltip(transaction))));
        row.append(DomBuilder.element('td', { class: `value-cell result ${sum < 0 ? 'negative' : 'positive'}` },
                Utils.createFxValue(sum, seen)));

        for (const f of filters.values()) {
            if (f.isHiddenInTable()) {
                continue;
            }
            if (f.isSynthetic()) {
                continue;
            }
            if (filter === f) {
                continue;
            }
            if (f.hasFilterFunc() && f.matches(transaction)) {
                descriptionCell.prepend(DomBuilder.span(' '));
                descriptionCell.prepend(Utils.createFilterLabel(f));
            }
        }

        return row;
    }
}
